/**
 * Cold-load fragment (#anchor) handling for the client-rendered app.
 *
 * The Razor views ship an empty `#root` (Views/Shared/_Layout.cshtml) and every anchor
 * target — `jumpToId` / `component-{key}` — is created by React only after
 * `window.pageData` has been read. The browser's native fragment scroll runs while the
 * document is still parsing, finds nothing, and never retries, so opening
 * `/page#section` in a new tab lands at the top of the page instead of on the section.
 *
 * In-page clicks are unaffected and do not go through here: JumpToSection and
 * ArticleContainer intercept those and scroll manually.
 */

// Sections can mount well after first paint (Suspense, locked-content gating, async
// blocks), so we watch for the target rather than checking once. Give up after this
// long — both to drop the observer and to avoid yanking the viewport around long
// after the visitor has settled on the page.
const TARGET_WAIT_MS = 3000

// A late jump is only welcome while the visitor is still waiting on the page. Any of
// these means they have taken control of the viewport, so we stand down. Deliberately
// not 'scroll': browser scroll restoration fires that without any user involvement.
const USER_INTENT_EVENTS = ['wheel', 'touchmove', 'keydown', 'mousedown']

const NO_CLEANUP = () => {}

/**
 * The id named by the current URL fragment, or '' when there is no fragment.
 * Editors can author anchors containing non-ASCII characters, which the browser
 * percent-encodes in `location.hash` but not in the rendered `id` attribute.
 */
export const getHashTargetId = () => {
  const raw = window.location.hash.slice(1)
  if (!raw) {
    return ''
  }
  try {
    return decodeURIComponent(raw)
  } catch {
    // Malformed escape sequence — the raw fragment is the best available guess
    return raw
  }
}

/**
 * `behavior: 'auto'` is deliberate. An animated scroll away from the top of a
 * just-loaded page is jarring, and instant scrolling honours the `scroll-mt-24`
 * offset already on every section wrapper, so this lands exactly where an in-page
 * jump link lands.
 */
const revealTarget = (el) => {
  el.scrollIntoView({ block: 'start', behavior: 'auto' })
  // Keyboard and screen reader users have to arrive in the section too — without
  // this, the next Tab press starts from the top of the document.
  if (!el.hasAttribute('tabindex')) {
    el.setAttribute('tabindex', '-1')
  }
  el.focus({ preventScroll: true })
}

/**
 * Scrolls to the element named by the current URL fragment as soon as it exists in
 * the DOM. Returns a cleanup function that cancels the wait.
 */
export function scrollToHashTarget() {
  const id = getHashTargetId()
  if (!id) {
    return NO_CLEANUP
  }

  let observer
  let timer

  const stop = () => {
    observer?.disconnect()
    clearTimeout(timer)
    USER_INTENT_EVENTS.forEach((eventName) => window.removeEventListener(eventName, stop))
  }

  const attempt = () => {
    const el = document.getElementById(id)
    if (!el) {
      return
    }
    // Stop watching before scrolling, so our own scroll cannot trip the abort.
    stop()
    revealTarget(el)
  }

  USER_INTENT_EVENTS.forEach((eventName) =>
    window.addEventListener(eventName, stop, { passive: true, once: true }),
  )
  timer = setTimeout(stop, TARGET_WAIT_MS)
  observer = new MutationObserver(attempt)
  observer.observe(document.body, { childList: true, subtree: true })
  attempt()

  return stop
}
